import * as React from 'react'

import type { NonFsdsLpProject } from '@/components/learning-path/non-fsds-learning-path-content'

interface NonFsdsLpProjectsSectionProps {
  title: string
  description: string
  projects: NonFsdsLpProject[]
}

export default function NonFsdsLpProjectsSection({ title, description, projects }: NonFsdsLpProjectsSectionProps) {
  return (
    <section
      data-testid="non-fsds-lp-projects-section"
      className="relative overflow-hidden border-y border-blue-100/80 bg-gradient-to-b from-sky-50/60 via-white to-white px-4 py-14 sm:px-6 sm:py-16 lg:py-20"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.08),transparent_50%)]" />

      <div className="relative mx-auto max-w-7xl">
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl md:text-4xl">{title}</h2>
          <p className="mt-3 text-base leading-relaxed text-slate-600 sm:text-lg">{description}</p>
        </div>

        {/* Horizontal scroll container */}
        <div className="relative mt-10">
          {/* Fade edges */}
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6 bg-gradient-to-r from-sky-50/60 to-transparent sm:w-8 lg:hidden" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 bg-gradient-to-l from-white to-transparent sm:w-8 lg:hidden" />

          <div className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-4 scrollbar-hide sm:-mx-6 sm:gap-5 sm:px-6 lg:mx-0 lg:grid lg:grid-cols-4 lg:gap-5 lg:overflow-visible lg:px-0 lg:pb-0">
            {projects.map((project, index) => (
              <article key={project.title} className="w-[280px] flex-shrink-0 snap-start sm:w-[300px] lg:w-auto">
                <div className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-blue-100/80 bg-white shadow-[0_4px_16px_rgba(15,23,42,0.04)] transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_12px_32px_-12px_rgba(37,99,235,0.2)]">
                  {/* Gradient accent bar */}
                  <div className="h-1.5 w-full bg-gradient-to-r from-blue-400 via-sky-300 to-blue-200" />

                  <div className="flex flex-1 flex-col p-5 sm:p-6">
                    <div className="mb-3 flex items-center gap-2.5">
                      <span className="inline-flex size-8 items-center justify-center rounded-full border border-blue-100 bg-blue-50 text-sm font-bold text-blue-700">
                        {index + 1}
                      </span>
                      <h3 className="text-lg font-bold leading-snug text-slate-900">{project.title}</h3>
                    </div>

                    <p className="flex-1 text-sm leading-relaxed text-slate-600">{project.description}</p>

                    {/* Skill tags */}
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {(Array.isArray(project.skills) ? project.skills : []).map(skill => (
                        <span
                          key={skill}
                          className="rounded-full bg-blue-50/80 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-blue-100"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
